import type { StateCreator } from 'zustand';
import {
  contentSourcesApi,
  type ContentSource,
  type ContentSourceInput,
  type ContentSourceStatus,
} from '../services/contentSourcesApi';

export interface ContentSourcesSlice {
  contentSources: ContentSource[];
  contentSourcesLoading: boolean;
  contentSourcesError: string | null;

  fetchContentSources: () => Promise<void>;
  createContentSource: (data: ContentSourceInput) => Promise<ContentSource>;
  updateContentSource: (
    id: number,
    patch: { status?: ContentSourceStatus; fetch_interval_hours?: number }
  ) => Promise<void>;
  deleteContentSource: (id: number) => Promise<void>;
  fetchSourceNow: (id: number) => Promise<void>;
}

export const createContentSourcesSlice: StateCreator<
  ContentSourcesSlice,
  [],
  [],
  ContentSourcesSlice
> = (set, get) => ({
  contentSources: [],
  contentSourcesLoading: false,
  contentSourcesError: null,

  fetchContentSources: async () => {
    set({ contentSourcesLoading: true, contentSourcesError: null });
    try {
      const { sources } = await contentSourcesApi.list();
      set({ contentSources: sources });
    } catch (e) {
      set({ contentSourcesError: (e as Error).message });
    } finally {
      set({ contentSourcesLoading: false });
    }
  },

  createContentSource: async (data) => {
    const created = await contentSourcesApi.create(data);
    await get().fetchContentSources();
    return created;
  },

  updateContentSource: async (id, patch) => {
    // Optimistisch, damit der Zeitplan-Umschalter nicht bei jedem Klick kurz zurueckspringt.
    set((s) => ({
      contentSources: s.contentSources.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
    await contentSourcesApi.update(id, patch);
  },

  deleteContentSource: async (id) => {
    await contentSourcesApi.remove(id);
    set((s) => ({ contentSources: s.contentSources.filter((c) => c.id !== id) }));
  },

  fetchSourceNow: async (id) => {
    await contentSourcesApi.fetchNow(id);
    await get().fetchContentSources();
  },
});
